import localforage from 'localforage'

import { loadLive2DModelPreview as generateLive2DPreview } from '@proj-airi/stage-ui-live2d/utils/live2d-preview'
import { loadVrmModelPreview as generateVrmPreview } from '@proj-airi/stage-ui-three/utils/vrm-preview'
import { until } from '@vueuse/core'
import { nanoid } from 'nanoid'
import { defineStore } from 'pinia'
import { ref } from 'vue'

import '@proj-airi/stage-ui-live2d/utils/live2d-zip-loader'
import '@proj-airi/stage-ui-live2d/utils/live2d-opfs-registration'

export enum DisplayModelFormat {
  Live2dZip = 'live2d-zip',
  Live2dDirectory = 'live2d-directory',
  VRM = 'vrm',
  PMXZip = 'pmx-zip',
  PMXDirectory = 'pmx-directory',
  PMD = 'pmd',
}

export type DisplayModel
  = | DisplayModelFile
    | DisplayModelURL

const assetsBaseUrl = (import.meta.env.VITE_MODEL_ASSETS_BASE_URL || '')
  .trim()
  .replace(/^VITE_MODEL_ASSETS_BASE_URL\s*=\s*/, '')
  .replace(/\/+$/, '')

function resolveAssetUrl(fallbackPath: string, absoluteUrl: string) {
  if (import.meta.env.DEV) {
    // In development, prefer local assets to avoid CORS issues with remote buckets
    return new URL(absoluteUrl, import.meta.url).href
  }
  if (assetsBaseUrl)
    return `${assetsBaseUrl}/${fallbackPath.replace(/^\/+$/, '')}`
  // Production fallback: use public directory structure
  if (import.meta.env.PROD) {
    const pathMap: Record<string, string> = {
      'live2d-models': 'live2d/models',
      'vrm-models': 'vrm/models',
    }
    const [prefix, ...rest] = fallbackPath.split('/')
    const mappedPrefix = pathMap[prefix] || prefix
    return `/assets/${mappedPrefix}/${rest.join('/')}`
  }
  // Development: use relative URL which Vite will resolve correctly
  return new URL(absoluteUrl, import.meta.url).href
}

const presetLive2dProUrl = resolveAssetUrl('live2d-models/hiyori_pro_zh.zip', '../assets/live2d/models/hiyori_pro_zh.zip')
const presetLive2dFreeUrl = resolveAssetUrl('live2d-models/hiyori_free_zh.zip', '../assets/live2d/models/hiyori_free_zh.zip')
const presetLive2dPreview = resolveAssetUrl('live2d-models/hiyori/preview.png', '../assets/live2d/models/hiyori/preview.png')
const presetVrmAvatarAUrl = resolveAssetUrl('vrm-models/VRoid-Hub/AvatarSample-A/AvatarSample_A.vrm', '../assets/vrm/models/AvatarSample-A/AvatarSample_A.vrm')
const presetVrmAvatarAPreview = resolveAssetUrl('vrm-models/VRoid-Hub/AvatarSample-A/preview.png', '../assets/vrm/models/AvatarSample-A/preview.png')
const presetVrmAvatarBUrl = resolveAssetUrl('vrm-models/VRoid-Hub/AvatarSample-B/AvatarSample_B.vrm', '../assets/vrm/models/AvatarSample-B/AvatarSample_B.vrm')
const presetVrmAvatarBPreview = resolveAssetUrl('vrm-models/VRoid-Hub/AvatarSample-B/preview.png', '../assets/vrm/models/AvatarSample-B/preview.png')

export interface DisplayModelFile {
  id: string
  format: DisplayModelFormat
  type: 'file'
  file: File
  name: string
  previewImage?: string
  importedAt: number
}

export interface DisplayModelURL {
  id: string
  format: DisplayModelFormat
  type: 'url'
  url: string
  name: string
  previewImage?: string
  importedAt: number
}

const displayModelsPresets: DisplayModel[] = [
  { id: 'preset-live2d-1', format: DisplayModelFormat.Live2dZip, type: 'url', url: presetLive2dProUrl, name: 'Hiyori (Pro)', previewImage: presetLive2dPreview, importedAt: 1733113886840 },
  { id: 'preset-live2d-2', format: DisplayModelFormat.Live2dZip, type: 'url', url: presetLive2dFreeUrl, name: 'Hiyori (Free)', previewImage: presetLive2dPreview, importedAt: 1733113886840 },
  { id: 'preset-vrm-1', format: DisplayModelFormat.VRM, type: 'url', url: presetVrmAvatarAUrl, name: 'AvatarSample_A', previewImage: presetVrmAvatarAPreview, importedAt: 1733113886840 },
  { id: 'preset-vrm-2', format: DisplayModelFormat.VRM, type: 'url', url: presetVrmAvatarBUrl, name: 'AvatarSample_B', previewImage: presetVrmAvatarBPreview, importedAt: 1733113886840 },
]

export const useDisplayModelsStore = defineStore('display-models', () => {
  const displayModels = ref<DisplayModel[]>([])

  const displayModelsFromIndexedDBLoading = ref(false)

  async function loadDisplayModelsFromIndexedDB() {
    await until(displayModelsFromIndexedDBLoading).toBe(false)

    displayModelsFromIndexedDBLoading.value = true
    const models = [...displayModelsPresets]

    try {
      await localforage.iterate<{ format: DisplayModelFormat, file: File, importedAt: number, previewImage?: string }, void>((val, key) => {
        if (key.startsWith('display-model-')) {
          models.push({ id: key, format: val.format, type: 'file', file: val.file, name: val.file.name, importedAt: val.importedAt, previewImage: val.previewImage })
        }
      })
    }
    catch (err) {
      console.error(err)
    }

    displayModels.value = models.sort((a, b) => b.importedAt - a.importedAt)
    displayModelsFromIndexedDBLoading.value = false
  }

  async function getDisplayModel(id: string) {
    await until(displayModelsFromIndexedDBLoading).toBe(false)
    const modelFromFile = await localforage.getItem<DisplayModelFile>(id)
    if (modelFromFile) {
      return modelFromFile
    }

    // Fallback to in-memory presets if not found in localforage
    return displayModelsPresets.find(model => model.id === id)
  }

  const loadLive2DModelPreview = (file: File) => generateLive2DPreview(file)

  async function loadVrmModelPreview(file: File) {
    return generateVrmPreview(file)
  }

  async function addDisplayModel(format: DisplayModelFormat, file: File) {
    await until(displayModelsFromIndexedDBLoading).toBe(false)
    const newDisplayModel: DisplayModelFile = { id: `display-model-${nanoid()}`, format, type: 'file', file, name: file.name, importedAt: Date.now() }

    if (format === DisplayModelFormat.Live2dZip) {
      const previewImage = await loadLive2DModelPreview(file)
      newDisplayModel.previewImage = previewImage
    }
    else if (format === DisplayModelFormat.VRM) {
      const previewImage = await loadVrmModelPreview(file)
      newDisplayModel.previewImage = previewImage
    }

    displayModels.value.unshift(newDisplayModel)

    localforage.setItem<DisplayModelFile>(newDisplayModel.id, newDisplayModel)
      .catch(err => console.error(err))
  }

  async function renameDisplayModel(id: string, name: string) {
    await until(displayModelsFromIndexedDBLoading).toBe(false)
    const displayModel = await localforage.getItem<DisplayModelFile>(id)
    if (!displayModel)
      return

    displayModel.name = name
  }

  async function removeDisplayModel(id: string) {
    await until(displayModelsFromIndexedDBLoading).toBe(false)
    await localforage.removeItem(id)
    displayModels.value = displayModels.value.filter(model => model.id !== id)
  }

  async function resetDisplayModels() {
    await loadDisplayModelsFromIndexedDB()
    const userModelIds = displayModels.value.filter(model => model.type === 'file').map(model => model.id)
    for (const id of userModelIds) {
      await removeDisplayModel(id)
    }

    displayModels.value = [...displayModelsPresets].sort((a, b) => b.importedAt - a.importedAt)
  }

  return {
    displayModels,
    displayModelsFromIndexedDBLoading,

    loadDisplayModelsFromIndexedDB,
    getDisplayModel,
    addDisplayModel,
    renameDisplayModel,
    removeDisplayModel,
    resetDisplayModels,
  }
})
